/**
 * Draft-day math: whose pick is it, when do I pick next, and who will still be
 * on the board when my turn comes back around.
 *
 * Everything here is pure so it can be reasoned about (and tested) without a
 * network call — the caller supplies the Sleeper draft object and the board.
 */

/** Sleeper slot name -> board positions that can fill it. */
export const SLOT_ELIGIBILITY = {
  QB: ['QB'],
  RB: ['RB'],
  WR: ['WR'],
  TE: ['TE'],
  K: ['K'],
  DEF: ['DST'],
  DST: ['DST'],
  FLEX: ['RB', 'WR', 'TE'],
  WRRB_FLEX: ['RB', 'WR'],
  REC_FLEX: ['WR', 'TE'],
  WRRB_WRT: ['RB', 'WR', 'TE'],
  SUPER_FLEX: ['QB', 'RB', 'WR', 'TE'],
  IDP_FLEX: [],
};

const BENCH_SLOTS = new Set(['BN', 'IR', 'TAXI']);

/** Positions a redraft board tracks, in the order we like to display them. */
export const SKILL_POSITIONS = ['QB', 'RB', 'WR', 'TE', 'K', 'DST'];

/* ------------------------------------------------------------------ *
 * Snake / linear pick order
 * ------------------------------------------------------------------ */

/**
 * Which draft slot (1..teams) owns a given overall pick number.
 * Handles snake, linear, and Sleeper's "reversal round" (3rd-round reversal).
 */
export const slotForPickNo = (pickNo, { teams, type = 'snake', reversalRound = 0 }) => {
  const n = Number(teams);
  const pick = Number(pickNo);
  if (!n || !pick || pick < 1) return null;

  const round = Math.ceil(pick / n);
  const indexInRound = (pick - 1) % n;

  if (type === 'linear') return indexInRound + 1;

  let forward = round % 2 === 1;
  if (reversalRound > 0 && round >= reversalRound) forward = !forward;

  return forward ? indexInRound + 1 : n - indexInRound;
};

/** Overall pick number for a slot in a given round — the inverse of slotForPickNo. */
export const pickNoForSlotRound = (slot, round, { teams, type = 'snake', reversalRound = 0 }) => {
  const n = Number(teams);
  const s = Number(slot);
  if (!n || !s || !round) return null;

  if (type === 'linear') return (round - 1) * n + s;

  let forward = round % 2 === 1;
  if (reversalRound > 0 && round >= reversalRound) forward = !forward;

  return (round - 1) * n + (forward ? s : n - s + 1);
};

/** Every overall pick number belonging to a slot, in order. */
export const pickNumbersForSlot = (slot, { teams, rounds, type = 'snake', reversalRound = 0 }) => {
  const total = Number(rounds);
  if (!slot || !total) return [];
  const picks = [];
  for (let round = 1; round <= total; round += 1) {
    const pickNo = pickNoForSlotRound(slot, round, { teams, type, reversalRound });
    if (pickNo) picks.push({ pickNo, round });
  }
  return picks;
};

/** `1.06` style label for an overall pick number. */
export const formatPickSlot = (pickNo, teams) => {
  const n = Number(teams);
  if (!n || !pickNo) return `#${pickNo ?? '—'}`;
  const round = Math.ceil(pickNo / n);
  const inRound = ((pickNo - 1) % n) + 1;
  return `${round}.${String(inRound).padStart(2, '0')}`;
};

/**
 * Normalize the pieces of a Sleeper draft we care about into one object.
 *
 * `draft_order` maps user_id -> slot and is set as soon as the order is drawn;
 * `slot_to_roster_id` only appears once the draft starts, so we also derive
 * slot -> roster from the league's rosters when we can.
 */
export const describeDraft = (draft, { teams = [], userId = '' } = {}) => {
  const settings = draft?.settings || {};
  const teamCount = Number(settings.teams) || teams.length || 0;
  const rounds = Number(settings.rounds) || 0;
  const type = draft?.type || 'snake';
  const reversalRound = Number(settings.reversal_round) || 0;

  const rosterIdByOwner = new Map(
    teams.filter((t) => t.ownerId).map((t) => [String(t.ownerId), String(t.rosterId)]),
  );

  const slotToRoster = new Map();
  for (const [slot, rosterId] of Object.entries(draft?.slot_to_roster_id || {})) {
    if (rosterId != null) slotToRoster.set(Number(slot), String(rosterId));
  }
  const slotToUser = new Map();
  for (const [uid, slot] of Object.entries(draft?.draft_order || {})) {
    slotToUser.set(Number(slot), String(uid));
    if (!slotToRoster.has(Number(slot))) {
      const rid = rosterIdByOwner.get(String(uid));
      if (rid) slotToRoster.set(Number(slot), rid);
    }
  }

  const teamByRoster = new Map(teams.map((t) => [String(t.rosterId), t]));
  const teamNameForSlot = (slot) => {
    const rid = slotToRoster.get(Number(slot));
    return teamByRoster.get(rid)?.teamName || (rid ? `Roster ${rid}` : `Slot ${slot}`);
  };

  const mySlot = userId
    ? Number(
        Object.entries(draft?.draft_order || {}).find(([uid]) => String(uid) === String(userId))?.[1],
      ) || null
    : null;

  return {
    draftId: draft?.draft_id || '',
    status: draft?.status || 'unknown',
    type,
    reversalRound,
    teams: teamCount,
    rounds,
    // Only set on auction drafts. The board's dollar column falls back to the
    // standard $200 when it is missing, and says so.
    budget: Number(settings.budget) || null,
    totalPicks: teamCount && rounds ? teamCount * rounds : null,
    startTime: draft?.start_time ? Number(draft.start_time) : null,
    scoringType: draft?.metadata?.scoring_type || '',
    mySlot,
    myRosterId: mySlot ? slotToRoster.get(mySlot) || null : null,
    slotToRoster,
    slotToUser,
    teamNameForSlot,
  };
};

/**
 * Normalize an ESPN league payload into the same shape `draftClock` expects.
 *
 * ESPN uses fantasy team ids where Sleeper uses roster ids; we treat them as
 * roster ids throughout the draft tool.
 */
export const describeEspnDraft = (league, { teams = [], myTeamId = '' } = {}) => {
  const settings = league?.settings || {};
  const draftSettings = settings?.draftSettings || {};
  const draftDetail = league?.draftDetail || {};
  const teamCount = teams.length || (league?.teams || []).length;
  const rounds =
    Number(draftSettings?.pickOrder?.length) ||
    Math.ceil((draftDetail.picks || []).length / Math.max(teamCount, 1)) ||
    16;

  const draftType = String(draftSettings?.type || 'SNAKE').toLowerCase();
  const type = draftType.includes('linear') ? 'linear' : 'snake';

  const slotToRoster = new Map();
  const teamByRoster = new Map(teams.map((t) => [String(t.rosterId), t]));
  for (const team of league?.teams || []) {
    const slot = Number(team?.draftOrder);
    if (slot) slotToRoster.set(slot, String(team.id));
  }

  const teamNameForSlot = (slot) => {
    const rid = slotToRoster.get(Number(slot));
    return teamByRoster.get(rid)?.teamName || (rid ? `Team ${rid}` : `Slot ${slot}`);
  };

  const mySlot =
    myTeamId != null
      ? Number(
          (league?.teams || []).find((team) => String(team.id) === String(myTeamId))?.draftOrder,
        ) || null
      : null;

  let status = 'pre_draft';
  if (draftDetail.drafted) status = 'complete';
  else if (draftDetail.inProgress) status = 'drafting';

  const startTime = draftSettings?.date ? Date.parse(draftSettings.date) : null;

  return {
    draftId: String(league?.id || ''),
    status,
    type,
    reversalRound: 0,
    teams: teamCount,
    rounds,
    budget: Number(draftSettings?.auctionBudget) || null,
    totalPicks: teamCount && rounds ? teamCount * rounds : null,
    startTime: Number.isFinite(startTime) ? startTime : null,
    scoringType: '',
    mySlot,
    myRosterId: myTeamId ? String(myTeamId) : null,
    slotToRoster,
    slotToUser: new Map(),
    teamNameForSlot,
  };
};

/**
 * Where the draft stands: who is on the clock, when I pick next, and how many
 * picks happen in between.
 */
export const draftClock = (info, picksMade) => {
  const made = Number(picksMade) || 0;
  const order = { teams: info.teams, type: info.type, reversalRound: info.reversalRound };
  const currentPickNo = made + 1;
  const isComplete = info.totalPicks ? currentPickNo > info.totalPicks : false;

  const onTheClockSlot = isComplete ? null : slotForPickNo(currentPickNo, order);
  const myPicks = info.mySlot
    ? pickNumbersForSlot(info.mySlot, { ...order, rounds: info.rounds })
    : [];

  const upcoming = myPicks.filter((p) => p.pickNo >= currentPickNo);
  const nextPick = upcoming[0] || null;
  const followingPick = upcoming[1] || null;

  return {
    currentPickNo: isComplete ? null : currentPickNo,
    currentRound: isComplete ? null : Math.ceil(currentPickNo / (info.teams || 1)),
    onTheClockSlot,
    onTheClockTeam: onTheClockSlot ? info.teamNameForSlot(onTheClockSlot) : null,
    isMyPick: Boolean(onTheClockSlot && info.mySlot && onTheClockSlot === info.mySlot),
    myPicks,
    nextPick,
    followingPick,
    /** Picks by other teams before my next turn (0 when I'm on the clock). */
    picksUntilMyTurn: nextPick ? Math.max(0, nextPick.pickNo - currentPickNo) : null,
    /** Picks between my next turn and the one after — the "wheel" gap. */
    picksBetweenMyTurns:
      nextPick && followingPick ? followingPick.pickNo - nextPick.pickNo - 1 : null,
    roundsRemaining: info.rounds ? info.rounds - Math.ceil(currentPickNo / (info.teams || 1)) + 1 : null,
    isComplete,
  };
};

/* ------------------------------------------------------------------ *
 * Who will still be there?
 * ------------------------------------------------------------------ */

/**
 * Normal CDF, good to ~1e-7. Enough for a draft board.
 */
const normalCdf = (z) => {
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989422804014327 * Math.exp(-z * z / 2);
  const p = d * t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 +
            t * (-1.821255978 + t * 1.330274429))));
  return z >= 0 ? 1 - p : p;
};

/**
 * Probability a player is gone before a given pick.
 *
 * Driven by the **market's** ADP and its real per-player dispersion, not by our
 * own ranking. That distinction is the whole game. This previously fed the
 * player's rank on our board into a logistic, which meant that any player we
 * rated far above the market was reported as about to be taken — so the tool
 * told you to reach on precisely the players you had an edge on, paying full
 * price for your own disagreement. Trey McBride sits ninth on our board and
 * 38th in ADP; the room is not taking him for another 28 picks.
 *
 * `stdev` is how far real drafts scatter around that ADP (about ±2 picks in
 * round one, ±17 by round eleven). Falling back to a width proportional to ADP
 * keeps unpriced players from looking falsely certain.
 */
export const goneProbability = (adp, targetPick, stdev) => {
  const a = Number(adp);
  const pick = Number(targetPick);
  if (!Number.isFinite(a) || !Number.isFinite(pick) || pick <= 0) return null;
  const width = Number.isFinite(Number(stdev)) && Number(stdev) > 0
    ? Number(stdev)
    : Math.max(2, a * 0.18);
  return Math.min(1, Math.max(0, normalCdf((pick - a) / width)));
};

export const availabilityLabel = (pGone) => {
  if (pGone >= 0.75) return { level: 'gone', text: 'Gone' };
  if (pGone >= 0.4) return { level: 'risky', text: 'Coin flip' };
  if (pGone >= 0.15) return { level: 'likely', text: 'Likely there' };
  return { level: 'safe', text: 'Safe' };
};

/**
 * Annotate the available board with survival odds for my next pick.
 *
 * `nextPick` is the overall pick number I am next on the clock for. Odds come
 * from each player's own ADP and dispersion, so a player the room rates far
 * below us correctly reads as safe to wait on.
 */
export const withAvailabilityOdds = (available, nextPick) =>
  available.map((row, index) => {
    const pGone = goneProbability(row.adp, nextPick, row.adpStdev);
    return {
      ...row,
      boardIndex: index,
      pGone,
      availability: pGone == null ? null : availabilityLabel(pGone),
    };
  });

/**
 * What this pick actually buys you, versus waiting one turn.
 *
 * Season-long value over replacement answers "how good is he". It is not a
 * draft decision. The decision is opportunity cost: if I spend this pick on
 * him, what do I give up against the best player at his position I could still
 * get next turn? A back worth +90 whose position falls off a cliff is a very
 * different pick from a back worth +90 with three comparable backs surviving.
 *
 * A player who is himself likely to survive costs you nothing to pass on, so
 * his wait cost is ~0 — that is the tool telling you to take the scarce player
 * instead and come back for this one.
 */
export const withWaitCost = (availableWithOdds, threshold = 0.5) => {
  const survivorValue = {};
  for (const row of availableWithOdds) {
    const pos = row.position;
    if (survivorValue[pos] !== undefined) continue;
    // Rows arrive best-first, so the first likely survivor at a position is the
    // best one we could still get next turn.
    if (row.pGone != null && row.pGone < threshold && row.vor != null) {
      survivorValue[pos] = row.vor;
    }
  }

  return availableWithOdds.map((row) => {
    if (row.vor == null || row.pGone == null) return { ...row, waitCost: null };
    const fallback = survivorValue[row.position];
    // No comparable survivor at all means the position is about to be stripped;
    // the whole surplus is at risk.
    const cost = fallback === undefined ? row.vor : row.vor - fallback;
    return { ...row, waitCost: Math.round(Math.max(0, cost) * 10) / 10 };
  });
};

/**
 * Value lost by waiting: the gap between the best player at a position now and
 * the best one likely to survive until my next turn. A big gap means the
 * position falls off a cliff before I pick again.
 */
export const valueOverNextTurn = (availableWithOdds, position) => {
  const atPosition = availableWithOdds.filter((r) => r.position === position);
  if (!atPosition.length) return null;

  const best = atPosition[0];
  const survivor = atPosition.find((r) => r.pGone < 0.5) || null;

  return {
    position,
    best,
    survivor,
    gap: survivor
      ? Math.max(0, (best.rankValue ?? best.value ?? 0) - (survivor.rankValue ?? survivor.value ?? 0))
      : best.rankValue ?? best.value ?? 0,
    /** True when even the best option at this position is unlikely to last. */
    cliff: !survivor || survivor.id !== best.id,
  };
};

/* ------------------------------------------------------------------ *
 * Roster shape from the league's own settings
 * ------------------------------------------------------------------ */

/** Break `roster_positions` into starter slots, bench count, and league format. */
export const parseRosterPositions = (rosterPositions = []) => {
  const starterSlots = [];
  let bench = 0;

  for (const slot of rosterPositions) {
    if (BENCH_SLOTS.has(slot)) {
      bench += 1;
      continue;
    }
    if (SLOT_ELIGIBILITY[slot]) starterSlots.push(slot);
  }

  const qbSlots = starterSlots.filter((s) => s === 'QB').length;
  const superflex = starterSlots.some((s) => s === 'SUPER_FLEX');

  return {
    starterSlots,
    bench,
    format: superflex || qbSlots > 1 ? 'sf' : 'oneqb',
    startersCount: starterSlots.length,
  };
};

/**
 * Fit my drafted players into the league's starting lineup, best value first,
 * so "needs" reflect the actual lineup rather than a generic template.
 */
export const fillStarters = (myPlayers, starterSlots) => {
  const remaining = [...myPlayers].sort((a, b) => b.value - a.value);
  const slots = starterSlots.map((slot, index) => ({
    key: `${slot}-${index}`,
    slot,
    eligible: SLOT_ELIGIBILITY[slot] || [],
    player: null,
  }));

  // Dedicated slots first (a TE should land in TE, not soak up the FLEX).
  const order = [...slots].sort((a, b) => a.eligible.length - b.eligible.length);

  for (const slotEntry of order) {
    const idx = remaining.findIndex((p) => slotEntry.eligible.includes(p.position));
    if (idx >= 0) {
      slotEntry.player = remaining.splice(idx, 1)[0];
    }
  }

  return { slots, benchPlayers: remaining };
};

/**
 * Depth targets for a redraft roster: every starting slot plus a sensible share
 * of the bench, weighted to the positions you actually stream least.
 */
export const depthTargets = ({ starterSlots, bench, format }) => {
  const targets = { QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DST: 0 };

  for (const slot of starterSlots) {
    const eligible = SLOT_ELIGIBILITY[slot] || [];
    if (eligible.length === 1) {
      targets[eligible[0]] += 1;
    } else if (eligible.includes('RB') && eligible.includes('WR')) {
      // Flex spots get split between the two positions that usually fill them.
      targets.RB += 0.5;
      targets.WR += 0.5;
    } else if (eligible.includes('QB')) {
      targets.QB += 1;
    }
  }

  // Bench priority: running back and receiver depth first, then a backup TE/QB.
  const benchPlan = format === 'sf' ? ['RB', 'WR', 'QB', 'RB', 'WR', 'TE'] : ['RB', 'WR', 'RB', 'WR', 'TE', 'QB'];
  for (let i = 0; i < bench; i += 1) {
    targets[benchPlan[i % benchPlan.length]] += 1;
  }

  return Object.fromEntries(Object.entries(targets).map(([k, v]) => [k, Math.round(v)]));
};

/**
 * Per-position needs combining unfilled starting slots (urgent) with depth
 * targets (nice to have).
 */
export const computeLineupNeeds = (myPlayers, rosterShape) => {
  const { slots } = fillStarters(myPlayers, rosterShape.starterSlots);
  const targets = depthTargets(rosterShape);

  const have = { QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DST: 0 };
  for (const p of myPlayers) {
    if (have[p.position] != null) have[p.position] += 1;
  }

  const openStarterPositions = {};
  for (const slotEntry of slots) {
    if (slotEntry.player) continue;
    for (const pos of slotEntry.eligible) {
      openStarterPositions[pos] = (openStarterPositions[pos] || 0) + 1;
    }
  }

  const needs = SKILL_POSITIONS.map((position) => {
    const target = targets[position] || 0;
    const held = have[position] || 0;
    const openStarters = openStarterPositions[position] || 0;
    const deficit = Math.max(0, target - held);

    let priority = 'balanced';
    if (openStarters > 0 && held === 0) priority = 'critical';
    else if (openStarters > 0) priority = 'high';
    else if (deficit >= 2) priority = 'medium';
    else if (held > target) priority = 'surplus';

    return { position, have: held, target, deficit, openStarters, priority };
  });

  return { needs, slots, openStarterPositions };
};

/* ------------------------------------------------------------------ *
 * Recommendations
 * ------------------------------------------------------------------ */

const NEED_MULTIPLIER = {
  critical: 1.18,
  high: 1.1,
  medium: 1.04,
  balanced: 1,
  surplus: 0.85,
};

/**
 * The number a row is ranked by. The board can be ordered by KTC value or by
 * points over replacement; every comparison here is relative, so the engine only
 * needs to agree with the board about which scale is in play.
 *
 * A null rankValue means "not on the projection scale at all" (kickers,
 * defenses, players too deep to be projected) and must not silently fall back to
 * the KTC scale, whose numbers are thousands of times larger.
 */
const rankValue = (row) => {
  if (row?.rankValue === null) return null;
  return (row?.rankValue !== undefined ? row.rankValue : row?.value) || 0;
};

/**
 * Where the last startable player at each position sits, given league size and
 * the starting lineup. Points above that baseline — not raw points — are what
 * make positions comparable: a QB projected for 310 is worth less than a back
 * projected for 240 when eleven other quarterbacks also clear 280.
 *
 * Splitting a flex slot evenly across its eligible positions is only a rough
 * approximation, and it was outright wrong for superflex: `SUPER_FLEX` is
 * eligible for RB and WR too, so the RB/WR branch claimed it and the QB branch
 * below could never run. A superflex league came out with the same quarterback
 * baseline as a one-quarterback league — in the one format where quarterbacks
 * matter most. Order the checks so the widest slot is recognised first.
 *
 * Prefer `startingPool` where projections are available; it fills the league
 * rather than guessing the split.
 */
export const replacementRanks = (rosterShape, teams) => {
  const n = Number(teams) || 12;
  const perTeam = { QB: 0, RB: 0, WR: 0, TE: 0 };

  for (const slot of rosterShape?.starterSlots || []) {
    const eligible = SLOT_ELIGIBILITY[slot] || [];
    if (eligible.length === 1) {
      if (perTeam[eligible[0]] != null) perTeam[eligible[0]] += 1;
    } else if (eligible.includes('QB')) {
      // Superflex. Quarterbacks take this slot almost every time, because they
      // outscore every other option in raw terms.
      perTeam.QB += 1;
    } else if (eligible.includes('RB') && eligible.includes('WR')) {
      perTeam.RB += 0.5;
      perTeam.WR += 0.5;
    } else if (eligible.includes('WR') && eligible.includes('TE')) {
      perTeam.WR += 0.5;
      perTeam.TE += 0.5;
    }
  }

  return Object.fromEntries(
    Object.entries(perTeam).map(([pos, count]) => [pos, Math.max(1, Math.round(count * n))]),
  );
};

/**
 * Baseline points at each position, found by actually filling every lineup.
 *
 * Dedicated slots first, then each flex slot goes to the best player still
 * eligible for it. That needs no guess about how flex slots split, and it is
 * what makes a superflex league reprice quarterbacks correctly: they win those
 * slots on points, so twice as many of them start and the baseline moves with
 * them.
 *
 * `pointsOf` selects which projection to use, so the same fill serves the
 * consensus board and our own model.
 */
export const startingPool = (board, rosterShape, teams, pointsOf = (row) => row.proj) => {
  const n = Number(teams) || 12;
  const dedicated = { QB: 0, RB: 0, WR: 0, TE: 0 };
  const flexSlots = [];

  for (const slot of rosterShape?.starterSlots || []) {
    const eligible = (SLOT_ELIGIBILITY[slot] || []).filter((p) => dedicated[p] != null);
    if (eligible.length === 1) dedicated[eligible[0]] += n;
    else if (eligible.length > 1) for (let i = 0; i < n; i += 1) flexSlots.push(eligible);
  }

  const ranked = {};
  for (const position of ['QB', 'RB', 'WR', 'TE']) {
    ranked[position] = board
      .filter((row) => row.position === position && pointsOf(row) != null)
      .map((row) => pointsOf(row))
      .sort((a, b) => b - a);
  }

  const taken = { ...dedicated };
  for (const position of Object.keys(taken)) {
    taken[position] = Math.min(taken[position], ranked[position].length);
  }

  for (const eligible of flexSlots) {
    let best = null;
    let bestPosition = null;
    for (const position of eligible) {
      const next = taken[position];
      if (next < ranked[position].length && (best === null || ranked[position][next] > best)) {
        best = ranked[position][next];
        bestPosition = position;
      }
    }
    if (bestPosition) taken[bestPosition] += 1;
  }

  const out = {};
  for (const position of Object.keys(taken)) {
    const pool = ranked[position];
    if (!pool.length) continue;
    out[position] = pool[Math.min(Math.max(taken[position], 1), pool.length) - 1];
  }
  return out;
};

/**
 * Projected points of the replacement-level player at each position, taken from
 * the board itself so it tracks whatever pool is actually in play.
 */
export const replacementPoints = (board, rosterShape, teams, pointsOf) =>
  startingPool(board, rosterShape, teams, pointsOf);

/** Kickers and defenses are a last-rounds problem, never a value pick. */
const lateOnlyPenalty = (position, roundsRemaining) => {
  if (!['K', 'DST'].includes(position)) return 1;
  if (roundsRemaining == null) return 0.35;
  if (roundsRemaining <= 4) return 0.6;
  return 0.15;
};

/**
 * Candidates worth scoring: the top of the board, plus the best few at every
 * position with an empty starting slot. Kickers and defenses carry tiny KTC
 * values, so without the second half they would never enter the pool at all —
 * which is exactly wrong in the last rounds, when they are the only picks left
 * that matter.
 */
const candidatePool = (availableWithOdds, needs, poolSize = 60) => {
  const pool = availableWithOdds.slice(0, poolSize);
  const seen = new Set(pool.map((r) => r.id));

  for (const need of needs) {
    if (!need.openStarters) continue;
    const best = availableWithOdds.filter((r) => r.position === need.position).slice(0, 3);
    for (const row of best) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      pool.push(row);
    }
  }

  return pool;
};

/**
 * Rank the board for *this* pick: raw KTC value, adjusted for what my roster
 * still needs, how fast the position is drying up, and whether the player will
 * even be there when my turn comes back.
 */
export const recommendPicks = ({
  availableWithOdds,
  needs = [],
  roundsRemaining = null,
  openStarterSlots = 0,
  limit = 5,
}) => {
  if (!availableWithOdds.length) return [];

  // Out of runway: with as many empty starting slots as picks left, filling
  // them stops being a value question and becomes mandatory.
  const mustFill = openStarterSlots > 0 && roundsRemaining != null && roundsRemaining <= openStarterSlots;

  const needByPosition = Object.fromEntries(needs.map((n) => [n.position, n]));
  const vonaByPosition = Object.fromEntries(
    SKILL_POSITIONS.map((pos) => [pos, valueOverNextTurn(availableWithOdds, pos)]),
  );

  const topValue = rankValue(availableWithOdds[0]) || 1;


  const scored = candidatePool(availableWithOdds, needs)
    .filter((row) => rankValue(row) !== null || (mustFill && needByPosition[row.position]?.openStarters > 0))
    .map((row) => {
    const need = needByPosition[row.position];
    const needFactor = NEED_MULTIPLIER[need?.priority] ?? 1;
    const vona = vonaByPosition[row.position];
    // A position about to fall off a cliff is worth taking early.
    const scarcityFactor = 1 + Math.min(0.3, (vona?.gap || 0) / (topValue * 1.5));
    const timing = lateOnlyPenalty(row.position, roundsRemaining);

    // Score what the pick BUYS, not how good the player is.
    //
    // Ranking on raw surplus recommends players the same tool is telling you to
    // wait on — Trey McBride went top-five here while his own row read "cheap
    // to wait", because a 12% urgency nudge cannot outweigh a big season-long
    // number. Wait cost already prices scarcity and survival together: it is
    // his surplus minus the best man at his position still there next turn.
    //
    // A quarter of raw surplus stays in as a floor, so that when nothing is
    // scarce the board still leads with the best players rather than going flat.
    const surplus = rankValue(row);
    const base = row.waitCost != null ? row.waitCost + 0.25 * (surplus || 0) : surplus;
    const score = base === null ? 0 : base * needFactor * scarcityFactor * timing;

    const required = mustFill && need?.openStarters > 0;

    const reasons = [];
    if (required) reasons.push(`must fill ${row.position} — ${roundsRemaining} round${roundsRemaining === 1 ? '' : 's'} left`);
    else if (need?.openStarters > 0) reasons.push(`fills your open ${row.position} starter`);
    else if (need?.deficit >= 2) reasons.push(`${row.position} depth (${need.have}/${need.target})`);
    if (vona?.cliff && vona.gap > topValue * 0.06) reasons.push(`${row.position} cliff before your next pick`);
    if (row.pGone >= 0.75) reasons.push('almost certainly gone if you wait');
    else if (row.pGone <= 0.15) reasons.push('should still be here next turn');
    if (row.waitCost != null && row.waitCost < 5 && row.pGone <= 0.4) {
      reasons.push('nothing lost by waiting a turn');
    }
    // Our own disagreement with the room, already gated on confidence.
    if (row.modelEdge > 10) reasons.push(`we have him ~${Math.round(row.modelEdge)} pts above the market`);
    else if (row.modelEdge < -10) reasons.push(`the room likes him ~${Math.abs(Math.round(row.modelEdge))} pts more than we do`);
    if (!reasons.length) reasons.push('best value on the board');

    return {
      ...row,
      score,
      required,
      reasons,
      needPriority: need?.priority || 'balanced',
    };
  });

  // Required picks jump the queue outright; everything else falls back to score.
  scored.sort((a, b) => {
    if (a.required !== b.required) return a.required ? -1 : 1;
    return b.score - a.score;
  });

  // A shortlist of five running backs is not a shortlist. Cap each position so
  // the list offers genuine alternatives, backfilling if that leaves it short.
  const perPosition = {};
  const shortlist = [];
  const overflow = [];

  for (const row of scored) {
    const count = perPosition[row.position] || 0;
    if (count < 2 && shortlist.length < limit) {
      perPosition[row.position] = count + 1;
      shortlist.push(row);
    } else {
      overflow.push(row);
    }
  }

  return shortlist.concat(overflow).slice(0, limit);
};

/** Countdown text for a draft that has not started yet. */
export const formatCountdown = (startTime, now = Date.now()) => {
  if (!startTime) return '';
  const ms = startTime - now;
  if (ms <= 0) return '';
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};
